import React, { useState, useEffect, useRef } from "react";
import {
  auth,
  signInWithPhoneNumber,
  linkWithPhoneNumber,
  RecaptchaVerifier,
  updateUserDatabase,
  getUserFromDatabase,
} from "shared/utils/firebase";
import { generateUniqueSiliconId } from "shared/utils/siliconId";
import { dispatchWelcomeEmail } from "shared/utils/emailService";
import { saveSessionToCookie } from "shared/utils/sessionBridge";
import { isPhoneTaken } from "shared/utils/userValidation";
import styles from "./PhoneOtpModal.module.css";

function PhoneOtpModal({
  isOpen,
  onClose,
  mode = "login", // "login" | "verify"
  initialPhone = "",
  currentUserUid = null,
  title = null,
  description = null,
  onSuccess = () => {},
}) {
  const [step, setStep] = useState("input"); // "input" | "otp"
  const [countryCode, setCountryCode] = useState("+91");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [timer, setTimer] = useState(60);

  const otpInputsRef = useRef([]);
  const recaptchaVerifierRef = useRef(null);
  const recaptchaContainerRef = useRef(null);

  // Sync initial phone and reset states whenever modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setStep("input");
      setOtp(["", "", "", "", "", ""]);
      setErrorMsg("");
      setLoading(false);
      setTimer(60);
      setConfirmationResult(null);

      // Parse initialPhone if present
      if (initialPhone) {
        const cleaned = initialPhone.trim();
        if (cleaned.startsWith("+91")) {
          setCountryCode("+91");
          setPhoneNumber(cleaned.replace(/^\+91/, "").replace(/^0+/, ""));
        } else if (cleaned.startsWith("+1")) {
          setCountryCode("+1");
          setPhoneNumber(cleaned.replace(/^\+1/, "").replace(/^0+/, ""));
        } else if (cleaned.startsWith("+44")) {
          setCountryCode("+44");
          setPhoneNumber(cleaned.replace(/^\+44/, "").replace(/^0+/, ""));
        } else if (cleaned.startsWith("+65")) {
          setCountryCode("+65");
          setPhoneNumber(cleaned.replace(/^\+65/, "").replace(/^0+/, ""));
        } else if (cleaned.startsWith("+971")) {
          setCountryCode("+971");
          setPhoneNumber(cleaned.replace(/^\+971/, "").replace(/^0+/, ""));
        } else {
          setPhoneNumber(cleaned.replace(/\D/g, "").replace(/^0+/, ""));
        }
      } else {
        setPhoneNumber("");
      }
    } else {
      // Clean up reCAPTCHA on modal close
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {}
        recaptchaVerifierRef.current = null;
      }
    }
  }, [isOpen, initialPhone]);

  // Timer countdown for OTP resend
  useEffect(() => {
    let interval = null;
    if (step === "otp" && timer > 0) {
      interval = setInterval(() => {
        setTimer((t) => t - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  if (!isOpen) return null;

  // Initialize RecaptchaVerifier
  const initRecaptcha = () => {
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch (e) {}
      recaptchaVerifierRef.current = null;
    }

    const container =
      recaptchaContainerRef.current ||
      document.getElementById("recaptcha-verifier-container");

    if (!container) {
      console.warn("[PhoneAuth] reCAPTCHA container element not found in DOM");
      return null;
    }

    try {
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        auth,
        container,
        {
          size: "invisible",
          callback: () => {
            // reCAPTCHA solved automatically
          },
          "expired-callback": () => {
            setErrorMsg("Verification widget expired. Please request a new OTP code.");
          },
        }
      );
      return recaptchaVerifierRef.current;
    } catch (err) {
      console.error("[PhoneAuth] Recaptcha setup error:", err);
      return null;
    }
  };

  // Send SMS OTP
  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    const cleanPhone = phoneNumber.replace(/\D/g, "").replace(/^0+/, "");
    if (cleanPhone.length < 7 || cleanPhone.length > 15) {
      setErrorMsg("Please enter a valid mobile number (e.g. 9876543210).");
      return;
    }

    const fullPhoneNumber = `${countryCode}${cleanPhone}`;
    setLoading(true);
    setErrorMsg("");

    try {
      const verifier = initRecaptcha();
      if (!verifier) {
        throw new Error("Unable to initialize secure verification widget.");
      }

      let confirmation;
      if (mode === "verify" && auth.currentUser) {
        // Prevent linking a phone that already belongs to another user
        const phoneTaken = await isPhoneTaken(fullPhoneNumber, currentUserUid || auth.currentUser?.uid);
        if (phoneTaken) {
          setErrorMsg("⚠️ This phone number is already registered to another SiliconLogix account.");
          setLoading(false);
          return;
        }

        // Link phone to existing user account (or verify new number if already linked)
        const isAlreadyLinked = auth.currentUser.providerData?.some((p) => p.providerId === "phone") || Boolean(auth.currentUser.phoneNumber);
        if (isAlreadyLinked) {
          confirmation = await signInWithPhoneNumber(auth, fullPhoneNumber, verifier);
        } else {
          try {
            confirmation = await linkWithPhoneNumber(
              auth.currentUser,
              fullPhoneNumber,
              verifier
            );
          } catch (linkErr) {
            if (linkErr.code === "auth/provider-already-linked") {
              confirmation = await signInWithPhoneNumber(auth, fullPhoneNumber, verifier);
            } else {
              throw linkErr;
            }
          }
        }
      } else {
        // Login or Signup with Phone
        confirmation = await signInWithPhoneNumber(
          auth,
          fullPhoneNumber,
          verifier
        );
      }

      setConfirmationResult(confirmation);
      setStep("otp");
      setTimer(60);
      setLoading(false);

      // Auto-focus first OTP input after render
      setTimeout(() => {
        if (otpInputsRef.current[0]) {
          otpInputsRef.current[0].focus();
        }
      }, 150);
    } catch (err) {
      console.error("[PhoneAuth] Error sending OTP:", err);
      let msg = "Failed to dispatch SMS OTP. Please check the number.";
      if (err.code === "auth/invalid-phone-number") {
        msg = "The mobile number entered is invalid. Please verify country code and digits.";
      } else if (err.code === "auth/missing-phone-number") {
        msg = "Please enter a mobile phone number.";
      } else if (err.code === "auth/quota-exceeded") {
        msg = "SMS daily quota exceeded for this Firebase project. Please check Firebase SMS billing.";
      } else if (err.code === "auth/too-many-requests") {
        msg = "Too many SMS requests sent from this device. Please try again later.";
      } else if (err.code === "auth/captcha-check-failed") {
        msg = "reCAPTCHA verification failed. Please try again.";
      } else if (err.code === "auth/invalid-app-credential") {
        msg = "Invalid app credential. Please check authorized domains in Firebase Console.";
      } else if (err.code === "auth/operation-not-allowed") {
        msg = "Phone sign-in is not enabled in Firebase Authentication. Please enable Phone in Firebase Console.";
      } else if (err.code === "auth/credential-already-in-use") {
        msg = "This phone number is already linked to another SiliconLogix account.";
      } else if (err.message) {
        msg = err.message;
      }

      setErrorMsg(msg);
      setLoading(false);

      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {}
        recaptchaVerifierRef.current = null;
      }
    }
  };

  // Handle digit change in 6-box input
  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);

    // Auto-advance to next box
    if (digit && index < 5 && otpInputsRef.current[index + 1]) {
      otpInputsRef.current[index + 1].focus();
    }
  };

  // Handle backspace navigation
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputsRef.current[index - 1].focus();
    }
  };

  // Handle paste full 6-digit OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pastedData) return;

    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);
    const nextIdx = Math.min(pastedData.length, 5);
    if (otpInputsRef.current[nextIdx]) {
      otpInputsRef.current[nextIdx].focus();
    }
  };

  // Verify OTP Code
  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    const code = otp.join("");
    if (code.length < 6) {
      setErrorMsg("Please enter the complete 6-digit verification code.");
      return;
    }

    if (!confirmationResult) {
      setErrorMsg("Session expired. Please request a new OTP code.");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const cleanPhone = phoneNumber.replace(/\D/g, "").replace(/^0+/, "");
      const fullPhoneNumber = `${countryCode}${cleanPhone}`;

      // Confirm OTP code with Firebase Auth
      const userCredential = await confirmationResult.confirm(code);
      const user = userCredential.user;

      if (mode === "verify") {
        const targetUid = currentUserUid || user?.uid || auth.currentUser?.uid;
        if (targetUid) {
          await updateUserDatabase(
            {
              phone: fullPhoneNumber,
              phoneVerified: true,
              phoneVerifiedAt: new Date(),
              updatedAt: new Date(),
            },
            targetUid
          );
        }
        setLoading(false);
        onSuccess({ phone: fullPhoneNumber, verified: true });
        onClose();
        return;
      }

      // Login / Signup mode via Phone
      if (user) {
        let userData = await getUserFromDatabase(user.uid);

        if (!userData) {
          const assignedSiliconId = await generateUniqueSiliconId();
          userData = {
            uid: user.uid,
            phone: fullPhoneNumber,
            phoneVerified: true,
            phoneVerifiedAt: new Date(),
            siliconId: assignedSiliconId,
            role: "Student",
            isAdmin: false,
            createdAt: new Date(),
            lastLogin: new Date(),
            isActive: true,
            welcomeEmailSent: false,
            name: `User ${cleanPhone.slice(-4)}`,
            email: user.email || "",
            onboardingCompleted: false,
            provider: "phone",
          };
          await updateUserDatabase(userData, user.uid);

          if (user.email) {
            dispatchWelcomeEmail({
              uid: user.uid,
              email: user.email,
              name: userData.name,
              siliconId: assignedSiliconId,
              role: "Student",
            });
          }
        } else {
          // Existing user - ensure siliconId & phoneVerified exists
          const updates = {};
          if (!userData.siliconId) {
            const assignedSiliconId = await generateUniqueSiliconId();
            updates.siliconId = assignedSiliconId;
            userData.siliconId = assignedSiliconId;
          }
          if (!userData.phoneVerified) {
            updates.phoneVerified = true;
            updates.phone = fullPhoneNumber;
          }
          if (Object.keys(updates).length > 0) {
            await updateUserDatabase(updates, user.uid);
          }
        }

        // Save cross-subdomain session bridge
        try {
          saveSessionToCookie({
            ...user,
            ...userData,
            toJSON: () => user.toJSON(),
          });
        } catch (e) {}

        // Broadcast profile update
        window.dispatchEvent(
          new CustomEvent("slx_user_profile_updated", { detail: userData })
        );

        setLoading(false);
        onSuccess(userData);
        onClose();
      }
    } catch (err) {
      console.error("[PhoneAuth] Error verifying OTP:", err);
      let msg = "Invalid verification code. Please check and try again.";
      if (err.code === "auth/invalid-verification-code") {
        msg = "The 6-digit OTP code entered is incorrect.";
      } else if (err.code === "auth/code-expired") {
        msg = "The verification code has expired. Please request a new OTP.";
      } else if (err.code === "auth/credential-already-in-use") {
        msg = "This phone number is already linked to another SiliconLogix account.";
      } else if (err.message) {
        msg = err.message;
      }
      setErrorMsg(msg);
      setLoading(false);
    }
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
          ✕
        </button>

        {/* Hidden reCAPTCHA container with ref & id */}
        <div
          ref={recaptchaContainerRef}
          id="recaptcha-verifier-container"
          style={{ display: "none" }}
        ></div>

        <div className={styles.iconWrapper}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
          </svg>
        </div>

        {step === "input" ? (
          <>
            <h3 className={styles.title}>
              {title || (mode === "verify" ? (initialPhone ? "Verify Mobile Number" : "Change Mobile Number") : "Mobile Phone Sign In")}
            </h3>
            <p className={styles.desc}>
              {description || "We'll send a 6-digit verification SMS OTP to your mobile phone number."}
            </p>

            {errorMsg && <div className={styles.errorBanner}>{errorMsg}</div>}

            <form onSubmit={handleSendOtp}>
              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="phone-input">
                  Mobile Phone Number
                </label>
                <div className={styles.phoneInputWrapper}>
                  <select
                    className={styles.countryCode}
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                  >
                    <option value="+91">🇮🇳 +91 (India)</option>
                    <option value="+1">🇺🇸 +1 (USA / Canada)</option>
                    <option value="+44">🇬🇧 +44 (UK)</option>
                    <option value="+65">🇸🇬 +65 (Singapore)</option>
                    <option value="+971">🇦🇪 +971 (UAE)</option>
                    <option value="+61">🇦🇺 +61 (Australia)</option>
                    <option value="+49">🇩🇪 +49 (Germany)</option>
                    <option value="+33">🇫🇷 +33 (France)</option>
                    <option value="+81">🇯🇵 +81 (Japan)</option>
                  </select>
                  <input
                    id="phone-input"
                    type="tel"
                    required
                    placeholder="98765 43210"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className={styles.phoneInput}
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                className={styles.submitBtn}
                disabled={loading}
              >
                {loading ? "Sending SMS OTP..." : "Send Verification SMS"}
              </button>
            </form>
          </>
        ) : (
          <>
            <h3 className={styles.title}>Enter 6-Digit SMS OTP</h3>
            <p className={styles.desc}>
              Enter the verification code dispatched to{" "}
              <strong>
                {countryCode} {phoneNumber}
              </strong>
              .
            </p>

            {errorMsg && <div className={styles.errorBanner}>{errorMsg}</div>}

            <form onSubmit={handleVerifyOtp}>
              <div className={styles.otpGrid} onPaste={handlePaste}>
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => (otpInputsRef.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(idx, e)}
                    className={styles.otpBox}
                  />
                ))}
              </div>

              <button
                type="submit"
                className={styles.submitBtn}
                disabled={loading || otp.join("").length < 6}
              >
                {loading ? "Verifying Code..." : "Verify & Continue"}
              </button>

              <div className={styles.resendRow}>
                <span>
                  {timer > 0
                    ? `Resend code in ${timer}s`
                    : "Didn't receive the SMS?"}
                </span>
                <button
                  type="button"
                  className={styles.resendBtn}
                  disabled={timer > 0 || loading}
                  onClick={handleSendOtp}
                >
                  Resend OTP
                </button>
              </div>

              <button
                type="button"
                className={styles.changeNumberBtn}
                onClick={() => setStep("input")}
              >
                Change mobile number
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default PhoneOtpModal;
